import { Maybe } from '@graphql-tools/utils'
import { getCookie } from 'cookies-next'
import { LOGIN_REDIRECT_URL, LOGIN_STARTED_ORIGIN, LOGIN_USER_EMAIL } from '@/constants/cookies'
import { paths } from '@/constants/paths'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { ContentOnlyLayout } from '@/layouts/ContentOnlyLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { useLegacyEffect } from '@/molecules/utils'
import { GuildMemberReason, getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { Offer } from '@/services/PifService'
import { useSafeTrack } from '@/utils/analytics'
import DatadogRumFull from '@/utils/datadog/DatadogRumFull'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { LoginOrigin } from '@/views/GuildJoinView/GuildEmailInput/GuildEmailInput'
import { GuildSignUpCreateAccountView } from '@/views/GuildSignUpCreateAccountView'

export interface GuildSignUpPageParams {
  offer: Offer
  locale: string
  userEmail: string
  offerId: string
  children: React.ReactNode
  loginStartedOrigin: Maybe<LoginOrigin>
  loginRedirectUrl: Maybe<string>
}

export const GuildSignUpCreateAccountPage: NextPageWithLayout<GuildSignUpPageParams> = ({
  userEmail,
  locale,
  loginStartedOrigin,
  loginRedirectUrl,
}) => {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()
  const title = t('angelGuildCreateAccountTitle', 'Angel Guild Create Account | Angel')
  const description = t(
    'angelStudiosGuildJoinInfoMetaDescriptionV2',
    'As an Angel Guild member, you can vote on upcoming shows, receive free tickets to every theatrical release, gain early access to shows, receive merchandise discounts, and continue to amplify light.',
  )

  useLegacyEffect(() => {
    track('Guild Sign Up Flow Create Password Started')
  }, [track])

  return (
    <>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1736351150/angel-studios/guild/amgel-guild-open-graph-241030.webp',
          cloudinaryTransformation: 'b_rgb:000000',
          description,
          locale,
          path: `${paths.guild.signup.createAccount}`,
          title,
        })}
      />
      <DatadogRumFull />
      <GuildSignUpCreateAccountView
        userEmail={userEmail}
        loginStartedOrigin={loginStartedOrigin}
        loginRedirectUrl={loginRedirectUrl}
      />
    </>
  )
}

GuildSignUpCreateAccountPage.getLayout = (page) => {
  return <ContentOnlyLayout>{page}</ContentOnlyLayout>
}

export const getServerSideProps = getServerSidePropsErrorHandler(async ({ locale, req, res, federationClient }) => {
  const guildStatus = await getGuildStatus(federationClient)
  const userEmail = getCookie(LOGIN_USER_EMAIL, { req, res }) || ''
  const loginStartedOrigin = getCookie(LOGIN_STARTED_ORIGIN, { req, res }) || null
  const loginRedirectUrl = getCookie(LOGIN_REDIRECT_URL, { req, res }) || null

  if (guildStatus && guildStatus.isGuildMember && guildStatus.guildMemberReason === GuildMemberReason.GUILD_SUBSCRIBE) {
    return {
      redirect: {
        destination: '/guild',
        permanent: false,
      },
    }
  }

  const translations = await loadTranslations(locale, ['common', 'pif', 'account', 'guild'])

  return {
    props: {
      locale,
      userEmail,
      loginStartedOrigin,
      loginRedirectUrl,
      ...translations,
    },
  }
})

export default GuildSignUpCreateAccountPage
